import React from 'react'
// libs
import moment from 'moment'
import { useStore } from 'react-redux'
import { getFormValues } from 'redux-form'
import { useMutation, useQuery } from '@apollo/client/react'
// hooks
import { usePlanItemNotification } from 'hooks/usePlanItemNotification'
import { useStrategyCompletionProcess } from 'hooks/useStrategyCompletionProcess'
import { useApolloClient, useDefaultObjectSecurity, useModalData } from 'hooks'
// graphql
import { PLAN_OBJECTIVE_DETAILS_QUERY } from 'graphql/planObjectives/queries'
import { PLAN_OBJECTIVE_UPDATE_MUTATION } from 'graphql/planObjectives/mutations'
// constants
import { Modals, ENTITIES, Forms, OBJECT_ENTITY_NAMES } from 'constants/index'
// types
import { NetworkStatus } from '@apollo/client'
import { IPlanObjective, SystemProcessObject } from 'types/graphql'
// context
import { ObjectiveContext } from 'context'
// lodash
import get from 'lodash/get'
import round from 'lodash/fp/round'
// utils
import {
  getFieldValues,
  getPlanFromEntity,
  getOptionsByEntity,
  getValueFromOption,
  getValuesFromOptions,
  getFieldsInitialValues,
  generateInitialOwnedByValue
} from 'utils'
import { handleError } from 'utils/errors/handlers'
import { getPlanTypeLevel } from 'utils/objective'
import { runPendingUploads } from 'utils/pendingFileUploads'

interface Props {
  entity?: { is: string } & any
}

const withObjective = (EnhancedComponent: React.FC<any>) => {
  const EnhancedWithObjective: React.FC<Props> = (props) => {
    const { client } = useApolloClient()

    const store = useStore()

    const { modalData } = useModalData({ modalType: Modals.OBJECTIVE_MINIMIZED_MODAL })

    const objectiveId = props.entity?.id || modalData?.id
    const parentPermissions = props.entity?.id ? props.entity.parentPermissions : get(modalData, 'parentPermissions')

    const {
      data: detailsQuery,
      refetch,
      networkStatus,
      loading
    } = useQuery(PLAN_OBJECTIVE_DETAILS_QUERY, {
      skip: !objectiveId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          ids: objectiveId
        }
      }
    })

    const [updateMutation] = useMutation(PLAN_OBJECTIVE_UPDATE_MUTATION)
    const runCompletionProcess = useStrategyCompletionProcess()

    // constants
    const entity: IPlanObjective = get(detailsQuery, 'planObjectives.data[0]')

    usePlanItemNotification(entity?.id, refetch)

    const plan = getPlanFromEntity(entity)
    const isLoading =
      !entity && ((loading && networkStatus !== NetworkStatus.refetch) || (!!objectiveId && modalData.isOpen))
    const planTypeLevel = getPlanTypeLevel(plan, entity)
    const levelTypeOptions = planTypeLevel.typeOptions || []
    const levelDescription = get(plan, 'planType.levels', []).find(({ id }) => id === planTypeLevel.id)?.description
    // the level's own description is the "no type option" choice, so the select always shows the current type
    const typeOptions = levelTypeOptions.length
      ? [
          { value: '', label: levelDescription || OBJECT_ENTITY_NAMES.OBJECTIVE },
          ...levelTypeOptions.map(({ id, label }) => ({ value: id, label }))
        ]
      : []
    const defaultObjectSecurities = useDefaultObjectSecurity({ objectApiName: 'objectives', planType: plan.planType })

    // methods
    const getInitialValues = async () => {
      const fieldInitialValues = await getFieldsInitialValues({
        fieldValues: get(entity, 'fieldValueMap', []),
        fields: get(entity, 'fields', []),
        client
      })

      return {
        ...fieldInitialValues,
        completionPercentage: round(entity.completionPercentage),
        description: entity.description,
        ownedByEmployeeOrPosition: generateInitialOwnedByValue(entity),
        plan: plan ? { value: plan.id, label: plan.name } : null,
        typeOption: typeOptions.find(({ value }) => value === entity.typeOptionId) || typeOptions[0] || null
      }
    }

    const update = async ({ variables }) => {
      await updateMutation({ variables })
    }

    const handleDetailsSubmit = async (formData) => {
      try {
        await runPendingUploads(Forms.OBJECTIVE_DETAILS_FORM)
        const data = getFormValues(Forms.OBJECTIVE_DETAILS_FORM)(store.getState()) || formData

        const prevPercentage = entity.completionPercentage
        const nextPercentage = data.completionPercentage
        const isCompleting = prevPercentage < 100 && nextPercentage === 100

        const actualCompletionDateId = entity.fields.find((field) => field.name === 'actualCompletionDate')?.id
        const baseFieldValues = getFieldValues(entity.fields, data, defaultObjectSecurities)
        const fieldValues =
          isCompleting && actualCompletionDateId
            ? [
                ...baseFieldValues.filter((fv) => fv?.fieldId !== actualCompletionDateId),
                { fieldId: actualCompletionDateId, value: moment().format('YYYY-MM-DD') }
              ]
            : baseFieldValues

        await update({
          variables: {
            id: entity.id,
            input: {
              fieldValues,
              description: data.description,
              memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
              memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
              ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
              ...(typeOptions.length > 0 && { typeOptionId: getValueFromOption(data.typeOption) || null })
            }
          }
        })

        await runCompletionProcess({
          entityId: entity.id,
          object: SystemProcessObject.Objective,
          prev: prevPercentage,
          next: nextPercentage
        })
      } catch (errors) {
        handleError(errors, 'withObjective.handleDetailsSubmit')
      }
    }

    if (!isLoading && !entity && !modalData.isOpen) {
      return null
    }

    const value = {
      plan,
      update,
      refetch,
      isLoading,
      planTypeLevel,
      typeOptions,
      getInitialValues,
      handleDetailsSubmit,
      entity: { ...entity, parentPermissions }
    }

    return (
      <ObjectiveContext.Provider value={value}>
        <EnhancedComponent {...props} />
      </ObjectiveContext.Provider>
    )
  }

  return EnhancedWithObjective
}

export default withObjective
